import { useEffect, useMemo, useState } from "react";
import { Activity, Building2, Phone, Radio, RefreshCw, Save, Search, Settings2, Shield } from "lucide-react";
import { api, apiErrToStr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "@/components/ui/sonner";

const AUTO_REFRESH_MS = 5000;

const CODEC_OPTIONS = ["AUTO", "PCMA", "PCMU", "G722", "G729", "OPUS"];

function qualityLabel(quality) {
  const state = String(quality?.state || "idle").toLowerCase();
  if (state === "excellent") return { label: "Excelente", className: "tag-online" };
  if (state === "good") return { label: "Boa", className: "tag-online" };
  if (state === "fair") return { label: "Regular", className: "tag-warn" };
  if (state === "poor") return { label: "Ruim", className: "tag-offline" };
  if (state === "measuring") return { label: "Medindo", className: "" };
  if (state === "waiting") return { label: "Aguardando", className: "" };
  return { label: "Sem chamada", className: "" };
}

function callStateLabel(state) {
  const normalized = String(state || "idle").toLowerCase();
  if (normalized === "ringing") return { label: "Tocando", className: "tag-warn" };
  if (normalized === "dialing") return { label: "Chamando", className: "tag-warn" };
  if (normalized === "active") return { label: "Em chamada", className: "tag-online" };
  if (normalized === "hold") return { label: "Em espera", className: "tag-warn" };
  return { label: "Livre", className: "" };
}

function toCodecArray(value) {
  if (Array.isArray(value)) return value.length ? value : ["AUTO"];
  const parsed = String(value || "AUTO")
    .split(",")
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
  return parsed.length ? [...new Set(parsed)] : ["AUTO"];
}

function branchOptionsForTenant(tenant) {
  const branches = Array.isArray(tenant?.branches) ? tenant.branches : [];
  return [
    { value: "all", label: "Todas as filiais" },
    { value: "", label: "Cliente principal" },
    ...branches
      .filter((branch) => branch?.enabled !== false)
      .map((branch) => ({ value: branch.id, label: branch.nome || branch.id })),
  ];
}

export default function ExtensionMaintenance() {
  const { user, tenants, activeTenantId } = useAuth();
  const isSuperAdmin = String(user?.role || "").toLowerCase() === "superadmin";
  const [ramais, setRamais] = useState([]);
  const [permissionClasses, setPermissionClasses] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [localTenants, setLocalTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [tenantFilter, setTenantFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      setTenantFilter(String(activeTenantId || "").trim());
    }
  }, [activeTenantId, isSuperAdmin]);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    try {
      const unscoped = isSuperAdmin ? { skipTenantScope: true } : undefined;
      const [ramaisResponse, classesResponse, registrationsResponse, tenantsResponse] = await Promise.all([
        api.get("/ramais", unscoped),
        api.get("/call-permissions", unscoped),
        api.get("/registrations", unscoped),
        api.get("/tenants", isSuperAdmin ? { skipTenantScope: true } : undefined),
      ]);
      setRamais(ramaisResponse.data || []);
      setPermissionClasses(classesResponse.data || []);
      setRegistrations(registrationsResponse.data?.extensions || []);
      setLocalTenants(tenantsResponse.data || []);
      setError("");
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || "Nao foi possivel carregar a central tecnica.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") {
        load(true);
      }
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const tenantList = useMemo(
    () => (isSuperAdmin ? localTenants : tenants),
    [isSuperAdmin, localTenants, tenants],
  );
  const tenantMap = useMemo(
    () => Object.fromEntries((tenantList || []).map((tenant) => [tenant.id, tenant])),
    [tenantList],
  );
  const permissionMap = useMemo(
    () => Object.fromEntries((permissionClasses || []).map((item) => [item.id, item])),
    [permissionClasses],
  );
  const registrationMap = useMemo(
    () => Object.fromEntries((registrations || []).map((row) => [row.id, row])),
    [registrations],
  );

  const enrichedRows = useMemo(() => (
    (ramais || []).map((row) => {
      const registration = registrationMap[row.id] || {};
      return {
        ...row,
        registration,
        online: !!registration.online,
        call_state: registration.call_state || "idle",
        quality: registration.quality || { state: "idle" },
        branch_name: registration.branch_name || (
          row.branch_id
            ? ((tenantMap[row.tenant_id]?.branches || []).find((branch) => branch.id === row.branch_id)?.nome || row.branch_id)
            : "Principal"
        ),
        tenant_name: tenantMap[row.tenant_id]?.nome || row.tenant_id || "-",
      };
    })
  ), [ramais, registrationMap, tenantMap]);

  const selectedTenant = tenantMap[tenantFilter] || null;
  const branchOptions = useMemo(
    () => (selectedTenant ? branchOptionsForTenant(selectedTenant) : [{ value: "all", label: "Todas as filiais" }]),
    [selectedTenant],
  );

  const filteredRows = useMemo(() => {
    const term = String(search || "").trim().toLowerCase();
    return enrichedRows.filter((row) => {
      if (isSuperAdmin && tenantFilter && String(row.tenant_id || "") !== String(tenantFilter)) return false;
      if (branchFilter !== "all" && String(row.branch_id || "") !== String(branchFilter)) return false;
      if (statusFilter === "online" && !row.online) return false;
      if (statusFilter === "offline" && row.online) return false;
      if (statusFilter === "busy" && !["active", "hold", "ringing", "dialing"].includes(String(row.call_state || "").toLowerCase())) return false;
      if (!term) return true;
      const haystack = [
        row.numero,
        row.nome,
        row.tenant_name,
        row.branch_name,
        row.registration?.domain,
        row.registration?.network_ip,
        permissionMap[row.permission_class_id]?.nome,
      ].join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [branchFilter, enrichedRows, isSuperAdmin, permissionMap, search, statusFilter, tenantFilter]);

  const selectedRow = useMemo(
    () => filteredRows.find((row) => String(row.id) === String(selectedId)) || enrichedRows.find((row) => String(row.id) === String(selectedId)) || null,
    [enrichedRows, filteredRows, selectedId],
  );

  useEffect(() => {
    if (!selectedRow && filteredRows.length) {
      setSelectedId(filteredRows[0].id);
      return;
    }
    if (selectedRow) {
      setForm({
        id: selectedRow.id,
        nome: selectedRow.nome || "",
        senha: selectedRow.senha || "",
        transporte: selectedRow.transporte || "udp",
        caller_id_interno: selectedRow.caller_id_interno || selectedRow.numero || "",
        caller_id_externo: selectedRow.caller_id_externo || selectedRow.caller_id || "",
        permission_class_id: selectedRow.permission_class_id || "",
        recording_mode: selectedRow.recording_mode || (selectedRow.recording_enabled ? "all" : "off"),
        codec: toCodecArray(selectedRow.codec),
        enabled: selectedRow.enabled !== false,
        busy_announcement_enabled: !!selectedRow.busy_announcement_enabled,
      });
    } else if (!filteredRows.length) {
      setForm(null);
      setSelectedId("");
    }
  }, [filteredRows, selectedRow]);

  const summary = useMemo(() => {
    const online = filteredRows.filter((row) => row.online).length;
    const busy = filteredRows.filter((row) => ["active", "hold", "ringing", "dialing"].includes(String(row.call_state || "").toLowerCase())).length;
    const classified = filteredRows.filter((row) => !!row.permission_class_id).length;
    return {
      total: filteredRows.length,
      online,
      busy,
      classified,
    };
  }, [filteredRows]);

  const allowedPermissions = useMemo(() => {
    const effectiveTenantId = selectedRow?.tenant_id || tenantFilter || user?.tenant_id || "";
    return [
      { id: "", nome: "Sem categoria definida" },
      ...(permissionClasses || []).filter((item) => item.enabled !== false && (!effectiveTenantId || item.tenant_id === effectiveTenantId)),
    ];
  }, [permissionClasses, selectedRow?.tenant_id, tenantFilter, user?.tenant_id]);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const toggleCodec = (codec) => {
    setForm((current) => {
      const selected = Array.isArray(current?.codec) ? current.codec : ["AUTO"];
      if (codec === "AUTO") {
        return { ...current, codec: ["AUTO"] };
      }
      const withoutAuto = selected.filter((item) => item !== "AUTO");
      const nextValues = withoutAuto.includes(codec)
        ? withoutAuto.filter((item) => item !== codec)
        : [...withoutAuto, codec];
      return { ...current, codec: nextValues.length ? nextValues : ["AUTO"] };
    });
  };

  const saveCurrent = async () => {
    if (!selectedRow || !form) return;
    setSaving(true);
    try {
      const payload = {
        ...selectedRow,
        nome: String(form.nome || "").trim(),
        senha: String(form.senha || "").trim(),
        transporte: String(form.transporte || "udp").trim().toLowerCase(),
        caller_id_interno: String(form.caller_id_interno || "").trim(),
        caller_id_externo: String(form.caller_id_externo || "").trim(),
        permission_class_id: String(form.permission_class_id || "").trim(),
        recording_mode: String(form.recording_mode || "off").trim(),
        recording_enabled: String(form.recording_mode || "off").trim() !== "off",
        codec: (() => {
          const selected = Array.isArray(form.codec) ? form.codec : ["AUTO"];
          if (!selected.length || selected.includes("AUTO")) return "AUTO";
          return [...new Set(selected)].join(",");
        })(),
        enabled: !!form.enabled,
        busy_announcement_enabled: !!form.busy_announcement_enabled,
      };
      await api.put(`/ramais/${selectedRow.id}`, payload);
      toast.success("Configuracao do ramal aplicada.");
      await load(true);
    } catch (err) {
      toast.error("Falha ao salvar o ramal.", {
        description: apiErrToStr(err?.response?.data?.detail) || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="extension-maintenance-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Central tecnica</div>
          <h1 className="text-2xl font-semibold tracking-tight">Manutencao de ramais</h1>
          <p className="text-sm text-[color:var(--text-secondary)] mt-1">
            Painel inicial para os tecnicos verificarem status, categoria, configuracoes e monitoramento dos ramais.
          </p>
        </div>
        <button type="button" className="sbc-btn" onClick={() => load(true)} disabled={refreshing}>
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Atualizar
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Ramais visiveis", value: summary.total, help: "Escopo filtrado", icon: Phone },
          { label: "Registrados", value: summary.online, help: "Online agora", icon: Radio },
          { label: "Em uso", value: summary.busy, help: "Tocando ou em chamada", icon: Activity },
          { label: "Com categoria", value: summary.classified, help: "Classe de permissao vinculada", icon: Shield },
        ].map((item) => (
          <div key={item.label} className="sbc-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{item.label}</div>
                <div className="text-3xl font-semibold mt-2">{item.value}</div>
                <div className="text-xs text-[color:var(--text-secondary)] mt-1">{item.help}</div>
              </div>
              <div className="w-10 h-10 rounded-sm border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] flex items-center justify-center">
                <item.icon size={16} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sbc-card p-4 space-y-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {isSuperAdmin && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Cliente</label>
              <select className="sbc-input" value={tenantFilter} onChange={(event) => { setTenantFilter(event.target.value); setBranchFilter("all"); }}>
                <option value="">Todos os clientes</option>
                {(tenantList || []).map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>{tenant.nome || tenant.id}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Filial</label>
            <select className="sbc-input" value={branchFilter} onChange={(event) => setBranchFilter(event.target.value)}>
              {branchOptions.map((option) => (
                <option key={`${option.value}-${option.label}`} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Status</label>
            <select className="sbc-input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">Todos</option>
              <option value="online">Registrados</option>
              <option value="offline">Offline</option>
              <option value="busy">Em atendimento</option>
            </select>
          </div>
          <div className="xl:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Buscar</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                className="sbc-input pl-9"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Filtrar por ramal, nome, IP, cliente, categoria..."
              />
            </div>
          </div>
        </div>
        {error && <div className="text-xs text-[color:var(--accent-red)] font-mono border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">! {error}</div>}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(360px,0.9fr)]">
        <div className="sbc-card overflow-hidden">
          <div className="px-4 py-3 border-b border-[color:var(--border-default)] flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">Ramais em manutencao</div>
              <div className="text-[11px] text-[color:var(--text-secondary)]">Selecione um ramal para alterar categoria ou configuracao.</div>
            </div>
            <div className="text-[11px] font-mono text-[color:var(--text-muted)]">{refreshing ? "sincronizando..." : `auto refresh ${AUTO_REFRESH_MS / 1000}s`}</div>
          </div>
          <div className="overflow-auto max-h-[72vh]">
            <table className="sbc-table">
              <thead>
                <tr>
                  {isSuperAdmin && <th>Cliente</th>}
                  <th>Filial</th>
                  <th>Ramal</th>
                  <th>Status</th>
                  <th>Qualidade</th>
                  <th>Categoria</th>
                  <th>IP / transporte</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={isSuperAdmin ? 7 : 6} className="text-center text-[color:var(--text-muted)] py-8">Carregando...</td></tr>
                ) : !filteredRows.length ? (
                  <tr><td colSpan={isSuperAdmin ? 7 : 6} className="text-center text-[color:var(--text-muted)] py-8">Nenhum ramal encontrado.</td></tr>
                ) : filteredRows.map((row) => {
                  const callState = callStateLabel(row.call_state);
                  const quality = qualityLabel(row.quality);
                  const selected = String(selectedId) === String(row.id);
                  return (
                    <tr
                      key={row.id}
                      className={`cursor-pointer ${selected ? "bg-[rgba(37,99,235,0.08)]" : ""}`}
                      onClick={() => setSelectedId(row.id)}
                    >
                      {isSuperAdmin && <td>{row.tenant_name}</td>}
                      <td>{row.branch_name || "Principal"}</td>
                      <td>
                        <div className="font-mono font-medium">{row.numero}</div>
                        <div className="text-[10px] text-[color:var(--text-muted)]">{row.nome || "Sem nome"}</div>
                      </td>
                      <td><span className={`sbc-tag ${row.online ? "tag-online" : "tag-offline"} ${callState.className}`}>{row.online ? callState.label : "Offline"}</span></td>
                      <td><span className={`sbc-tag ${quality.className}`}>{quality.label}</span></td>
                      <td>
                        {row.permission_class_id ? (
                          <span className="text-xs">{permissionMap[row.permission_class_id]?.nome || "Categoria vinculada"}</span>
                        ) : (
                          <span className="text-xs text-[color:var(--text-muted)]">Sem categoria</span>
                        )}
                      </td>
                      <td className="font-mono text-xs">
                        <div>{row.registration?.network_ip || "-"}</div>
                        <div className="text-[10px] text-[color:var(--text-muted)]">{String(row.transporte || "udp").toUpperCase()}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="sbc-card p-4 space-y-4">
          {!selectedRow || !form ? (
            <div className="min-h-[420px] flex items-center justify-center text-sm text-[color:var(--text-muted)]">
              Selecione um ramal para abrir o painel tecnico.
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Ramal selecionado</div>
                  <h2 className="text-xl font-semibold mt-1">{selectedRow.numero} - {selectedRow.nome || "Sem nome"}</h2>
                  <div className="text-sm text-[color:var(--text-secondary)] mt-1">
                    {isSuperAdmin ? `${selectedRow.tenant_name} · ` : ""}{selectedRow.branch_name || "Principal"}
                  </div>
                </div>
                <button type="button" className="sbc-btn" onClick={() => window.location.assign("/ramais/cadastro")}>
                  <Settings2 size={14} />
                  Cadastro completo
                </button>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-sm border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Monitoramento</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className={`sbc-tag ${selectedRow.online ? "tag-online" : "tag-offline"}`}>{selectedRow.online ? "Registrado" : "Offline"}</span>
                    <span className={`sbc-tag ${callStateLabel(selectedRow.call_state).className}`}>{callStateLabel(selectedRow.call_state).label}</span>
                    <span className={`sbc-tag ${qualityLabel(selectedRow.quality).className}`}>{qualityLabel(selectedRow.quality).label}</span>
                  </div>
                  <div className="mt-3 text-xs text-[color:var(--text-secondary)] space-y-1">
                    <div><strong>Dominio:</strong> <span className="font-mono">{selectedRow.registration?.domain || selectedRow.sip_domain || "-"}</span></div>
                    <div><strong>IP:</strong> <span className="font-mono">{selectedRow.registration?.network_ip || "-"}</span></div>
                    <div><strong>Porta:</strong> <span className="font-mono">{selectedRow.registration?.network_port || selectedRow.sip_port || "-"}</span></div>
                    <div><strong>Aparelho:</strong> <span className="font-mono">{selectedRow.registration?.user_agent || "-"}</span></div>
                  </div>
                </div>
                <div className="rounded-sm border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Acoes rapidas</div>
                  <div className="mt-3 grid gap-2">
                    <button type="button" className="sbc-btn justify-start" onClick={() => updateField("enabled", !form.enabled)}>
                      <Phone size={14} />
                      {form.enabled ? "Desativar ramal" : "Ativar ramal"}
                    </button>
                    <button type="button" className="sbc-btn justify-start" onClick={() => updateField("busy_announcement_enabled", !form.busy_announcement_enabled)}>
                      <Activity size={14} />
                      {form.busy_announcement_enabled ? "Aviso de ocupado ativo" : "Ativar aviso de ocupado"}
                    </button>
                    <button type="button" className="sbc-btn justify-start" onClick={() => window.location.assign("/sistema/monitoramento-registros")}>
                      <Radio size={14} />
                      Abrir monitoramento completo
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Nome</label>
                  <input className="sbc-input" value={form.nome} onChange={(event) => updateField("nome", event.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Senha SIP</label>
                  <input className="sbc-input font-mono" value={form.senha} onChange={(event) => updateField("senha", event.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Categoria do ramal</label>
                  <select className="sbc-input" value={form.permission_class_id} onChange={(event) => updateField("permission_class_id", event.target.value)}>
                    {allowedPermissions.map((item) => (
                      <option key={item.id || "empty"} value={item.id}>{item.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Conexao SIP</label>
                  <select className="sbc-input" value={form.transporte} onChange={(event) => updateField("transporte", event.target.value)}>
                    <option value="udp">UDP</option>
                    <option value="tcp">TCP</option>
                    <option value="tls">TLS</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Caller ID interno</label>
                  <input className="sbc-input font-mono" value={form.caller_id_interno} onChange={(event) => updateField("caller_id_interno", event.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Caller ID externo</label>
                  <input className="sbc-input font-mono" value={form.caller_id_externo} onChange={(event) => updateField("caller_id_externo", event.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Gravacao</label>
                  <select className="sbc-input" value={form.recording_mode} onChange={(event) => updateField("recording_mode", event.target.value)}>
                    <option value="off">Nao gravar</option>
                    <option value="all">Todas as ligacoes</option>
                    <option value="internal">Somente internas</option>
                    <option value="external">Somente externas</option>
                  </select>
                </div>
                <div className="flex items-center gap-6 pt-6">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.enabled} onChange={(event) => updateField("enabled", event.target.checked)} className="w-4 h-4" />
                    <span>Ramal ativo</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.busy_announcement_enabled} onChange={(event) => updateField("busy_announcement_enabled", event.target.checked)} className="w-4 h-4" />
                    <span>Aviso de ocupado</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-2">Codecs</label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {CODEC_OPTIONS.map((codec) => {
                    const checked = (form.codec || []).includes(codec);
                    return (
                      <label key={codec} className="flex items-center gap-2 rounded-sm border border-[color:var(--border-default)] px-3 py-2 text-sm">
                        <input type="checkbox" checked={checked} onChange={() => toggleCodec(codec)} className="w-4 h-4" />
                        <span className="font-mono">{codec}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end">
                <button type="button" className="sbc-btn sbc-btn-primary" onClick={saveCurrent} disabled={saving}>
                  <Save size={14} />
                  {saving ? "Salvando..." : "Salvar configuracoes"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
